import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Globe, Languages } from 'lucide-react';
import { cn } from '@/lib/utils';
import { UI_LANGUAGES, currentUiLangCode } from '@/lib/ui-languages';
import { useLanguageStore, type Language as AppLanguage } from '@/stores/language.store';

type IconVariant = 'globe' | 'languages';

type Props = {
  /** `languages` évite la confusion avec un bouton « Publier » qui utilise déjà Globe. */
  icon?: IconVariant;
  iconClassName?: string;
  buttonClassName?: string;
  /** Position horizontale du menu déroulant */
  menuAlign?: 'end' | 'start';
  /** z-index du panneau (header / modales — défaut au-dessus du contenu principal) */
  menuZIndexClass?: string;
  title?: string;
};

export function UiLanguageMenu({
  icon = 'globe',
  iconClassName = 'h-5 w-5',
  buttonClassName,
  menuAlign = 'end',
  menuZIndexClass = 'z-[200]',
  title,
}: Props) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const menuPanelRef = useRef<HTMLDivElement>(null);
  const [menuRect, setMenuRect] = useState<{ top: number; left: number; width: number } | null>(null);
  const activeCode = currentUiLangCode(i18n.language);
  const IconCmp = icon === 'languages' ? Languages : Globe;

  const updateMenuRect = () => {
    const el = containerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setMenuRect({ top: r.bottom + 8, left: r.left, width: r.width });
  };

  useLayoutEffect(() => {
    if (!open) return;
    updateMenuRect();
    const onScrollOrResize = () => updateMenuRect();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open]);

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      const node = e.target as Node;
      if (containerRef.current?.contains(node)) return;
      if (menuPanelRef.current?.contains(node)) return;
      setOpen(false);
      setMenuRect(null);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  const handleSelect = (code: string) => {
    const short = code.split('-')[0] as AppLanguage;
    useLanguageStore.getState().setLanguage(short);
    void i18n.changeLanguage(short);
    setOpen(false);
    setMenuRect(null);
  };

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => {
          setOpen((wasOpen) => {
            if (wasOpen) {
              setMenuRect(null);
              return false;
            }
            const el = containerRef.current;
            if (el) {
              const r = el.getBoundingClientRect();
              setMenuRect({ top: r.bottom + 8, left: r.left, width: r.width });
            }
            return true;
          });
        }}
        className={buttonClassName}
        title={title}
        aria-label={t('header.language', 'Language')}
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <IconCmp className={iconClassName} />
      </button>
      {open &&
        menuRect &&
        createPortal(
          <div
            ref={menuPanelRef}
            role="listbox"
            dir="ltr"
            style={{
              position: 'fixed',
              top: menuRect.top,
              // « end » = côté fin du texte : à droite en LTR, à gauche en RTL (sinon le menu sort de l’écran en arabe).
              ...((menuAlign === 'end') !== (document.documentElement.dir === 'rtl')
                ? { right: Math.max(8, window.innerWidth - (menuRect.left + menuRect.width)) }
                : { left: Math.max(8, menuRect.left) }),
            }}
            className={cn(
              'min-w-[11rem] w-max max-w-[min(20rem,calc(100vw-1rem))] rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-600 dark:bg-gray-800',
              menuZIndexClass,
            )}
          >
            {UI_LANGUAGES.map((lang) => (
              <button
                key={lang.code}
                type="button"
                role="option"
                aria-selected={activeCode === lang.code}
                onClick={() => handleSelect(lang.code)}
                className={cn(
                  'flex w-full items-center px-4 py-2 text-start text-sm transition-colors',
                  activeCode === lang.code
                    ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700',
                )}
              >
                {lang.label}
              </button>
            ))}
          </div>,
          document.body,
        )}
    </div>
  );
}
